/**
 * Leaderboard Service
 * Handles all leaderboard-related API calls
 */

import apiClient from "../client";
import { ENDPOINTS } from "../endpoints";

/**
 * Fetch top users for leaderboard
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of users to fetch (default: 10)
 * @param {number} params.offset - Pagination offset (default: 0)
 * @returns {Promise<Array>} - Array of leaderboard entries
 */
export const getLeaderboard = async (params = {}) => {
  const { limit = 10, page = 1 } = params;

  try {
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    }).toString();

    const response = await apiClient.get(`${ENDPOINTS.USER.TOP}?${query}`);

    // Extract users array and metadata from response
    const users = Array.isArray(response)
      ? response
      : response.data || response.users || [];
    // Use userCount from API for total number of users, with fallback to users.length
    const totalCount = response.userCount || response.total || response.meta?.total || users.length;
    // Use pageCount from API if available, otherwise calculate from userCount and limit
    const totalPages = response.pageCount || Math.ceil(totalCount / limit);

    // Transform backend data to match frontend component expectations
    const transformedData = users.map((user) => ({
      rank: user.rank,
      name: user.twitterName || user.address,
      username: user.twitterUsername || user.address,
      handle: `@${user.twitterUsername || user.address}`,
      avatar: user.twitterProfilePictureUrl || "👤", // Use actual profile picture URL or fallback emoji
      reach: user.twitterFollowers ? user.twitterFollowers : 0, // Store as number for sorting
      reachFormatted: user.twitterFollowers ? user.twitterFollowers : 0,
      engagement: user.engagementScore ? user.engagementScore : 0, // Store as number for sorting
      engagementFormatted: user.engagementScore
        ? `${user.engagementScore.toFixed(1)}x`
        : "0x",
      totalScore: user.score ? user.score : 0, // Store as number for sorting
      totalScoreFormatted: user.score ? user.score.toFixed(2) : 0,
      change: user.scoreDiff ? user.scoreDiff : 0,
      verified: false, // TODO: Add verified status from backend if available
      mindshare: user.mindshare
        ? typeof user.mindshare === "number"
          ? user.mindshare
          : parseFloat(user.mindshare) || 0
        : 0,
      mindshareFormatted: user.mindshare
        ? typeof user.mindshare === "number"
          ? `${user.mindshare.toFixed(1)}%`
          : typeof user.mindshare === "string" && user.mindshare.includes("%")
            ? user.mindshare
            : `${parseFloat(user.mindshare || 0).toFixed(1)}%`
        : "0%",
      address: user.address,
      reachScore: user.reachScore || 0,
      engagementScore: user.engagementScore || 0,
      multiplier: user.multiplier || 1,
      score: user.score || 0,
      twitterProfilePictureUrl: user.twitterProfilePictureUrl || null,
      xpScore: user.xpScore || 0,
      xpScoreFormatted: user.xpScore ? user.xpScore.toLocaleString() : "0",
    }));

    return {
      data: transformedData,
      total: totalCount,
      totalPages,
      page,
      limit,
    };
  } catch (error) {
    console.error(
      "[LeaderboardService] Error fetching leaderboard:",
      error.message,
    );
    // Re-throw error to be handled by the UI
    throw error;
  }
};

/**
 * Fetch a specific user's rank and stats
 * @param {string} userId - User ID or wallet address
 * @returns {Promise<Object>} - User's leaderboard entry
 */
export const getUserRank = async (userId) => {
  try {
    const response = await apiClient.get(`${ENDPOINTS.USER.TOP}/${userId}`);
    return response.data;
  } catch (error) {
    console.error(
      "[LeaderboardService] Error fetching user rank:",
      error.message,
    );
    throw error;
  }
};

const leaderboardService = {
  getLeaderboard,
  getUserRank,
};

export default leaderboardService;
